import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import Chip from './Chip';
import Label from './Label';

/**
 * SearchInputWithChips Component - Search and select items with chip display
 *
 * @param {Object} props
 * @param {string} props.label - Label text for the input
 * @param {string} [props.placeholder='Search...'] - Placeholder text
 * @param {Array<{id: string, name: string}>} props.selectedItems - Array of selected items
 * @param {Function} props.onSearch - Callback when user types (query)
 * @param {Function} props.onSelect - Callback when item selected from dropdown ({id, name})
 * @param {Function} props.onRemove - Callback when chip removed (id)
 * @param {Array<{id: string, name: string}>} [props.searchResults=[]] - Search results for autocomplete
 * @param {boolean} [props.loading=false] - Whether search is loading
 * @param {string} [props.error] - Error message
 * @param {boolean} [props.disabled=false] - Whether the input is disabled
 * @param {string} [props.id] - ID for the input
 * @param {string} [props.className] - Additional CSS class
 */
const SearchInputWithChips = ({
  label,
  placeholder = 'Search...',
  selectedItems = [],
  onSearch,
  onSelect,
  onRemove,
  searchResults = [],
  loading = false,
  error,
  disabled = false,
  id,
  className,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowDropdown(value.length >= 2);
    setFocusedIndex(-1);

    if (onSearch) {
      onSearch(value);
    }
  };

  const handleSelectItem = (item) => {
    // Check if already selected
    if (selectedItems.some(selected => selected.id === item.id)) {
      return;
    }

    if (onSelect) {
      onSelect(item);
    }

    // Clear search and close dropdown
    setSearchQuery('');
    setShowDropdown(false);
    setFocusedIndex(-1);

    // Focus back on input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleRemoveChip = (_id) => {
    if (onRemove) {
      onRemove(_id);
    }
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || searchResults.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev =>
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;

      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < searchResults.length) {
          handleSelectItem(searchResults[focusedIndex]);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        setFocusedIndex(-1);
        break;

      default:
        break;
    }
  };

  const handleInputFocus = () => {
    if (searchQuery.length >= 2) {
      setShowDropdown(true);
    }
  };

  const labelId = id ? `${id}-label` : undefined;

  return (
    <Container ref={containerRef} className={className}>
      <Label text={label} htmlFor={id} />

      <InputContainer $disabled={disabled} $error={!!error}>
        {/* Search Icon */}
        <SearchIcon aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M13 13L9.5 9.5M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </SearchIcon>

        {/* Selected Chips */}
        <ChipsContainer>
          {selectedItems.map(item => (
            <Chip
              key={item.id}
              label={item.name}
              variant="input"
              selected={true}
              onRemove={() => handleRemoveChip(item.id)}
              disabled={disabled}
            />
          ))}

          {/* Search Input */}
          <SearchInput
            ref={inputRef}
            id={id}
            type="text"
            value={searchQuery}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            placeholder={selectedItems.length === 0 ? placeholder : ''}
            disabled={disabled}
            aria-labelledby={labelId}
            aria-autocomplete="list"
            aria-controls={showDropdown ? `${id}-dropdown` : undefined}
            aria-expanded={showDropdown}
          />
        </ChipsContainer>
      </InputContainer>

      {/* Autocomplete Dropdown */}
      {showDropdown && (searchResults.length > 0 || loading) && (
        <Dropdown
          ref={dropdownRef}
          id={`${id}-dropdown`}
          role="listbox"
          aria-label="Search results"
        >
          {loading ? (
            <DropdownItem $loading>
              <LoadingText>Searching...</LoadingText>
            </DropdownItem>
          ) : (
            searchResults.map((item, index) => {
              const isAlreadySelected = selectedItems.some(
                selected => selected.id === item.id
              );

              return (
                <DropdownItem
                  key={item.id}
                  role="option"
                  aria-selected={focusedIndex === index}
                  $focused={focusedIndex === index}
                  $disabled={isAlreadySelected}
                  onClick={() => !isAlreadySelected && handleSelectItem(item)}
                  onMouseEnter={() => setFocusedIndex(index)}
                >
                  {item.name}
                  {isAlreadySelected && (
                    <SelectedBadge>Selected</SelectedBadge>
                  )}
                </DropdownItem>
              );
            })
          )}
        </Dropdown>
      )}

      {/* No results message */}
      {showDropdown && !loading && searchQuery.length >= 2 && searchResults.length === 0 && (
        <Dropdown>
          <DropdownItem $noResults>
            <NoResultsText>No interpreters found</NoResultsText>
          </DropdownItem>
        </Dropdown>
      )}

      {/* Error message */}
      {error && (
        <ErrorMessage role="alert">
          {error}
        </ErrorMessage>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const InputContainer = styled.div`
  /* Layout */
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  min-height: 56px;

  /* Background and border */
  background: #ffffff;
  border: 1px solid ${props => props.$error ? '#e03333' : 'rgba(69, 68, 107, 0.3)'};
  border-radius: 4px;

  /* States */
  opacity: ${props => props.$disabled ? 0.5 : 1};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'text'};

  /* Focus within */
  &:focus-within {
    border-color: ${props => props.$error ? '#e03333' : 'rgba(69, 68, 107, 0.4)'};
    outline: none;
  }
`;

const SearchIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  color: #45446b;
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const SearchInput = styled.input`
  /* Layout */
  flex: 1;
  min-width: 100px;
  border: none;
  outline: none;
  background: transparent;

  /* Typography */
  font-family: 'Montserrat', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0.25px;
  color: #45446b;

  /* Remove default styles */
  padding: 0;

  /* Placeholder */
  &::placeholder {
    color: rgba(69, 68, 107, 0.4);
  }

  /* Disabled state */
  &:disabled {
    cursor: not-allowed;
  }
`;

const Dropdown = styled.div`
  /* Position */
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;

  /* Layout */
  max-height: 200px;
  overflow-y: auto;

  /* Appearance */
  background: #ffffff;
  border: 1px solid rgba(69, 68, 107, 0.2);
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const DropdownItem = styled.div`
  /* Layout */
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;

  /* Typography */
  font-family: 'Montserrat', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;
  color: ${props => props.$disabled ? 'rgba(69, 68, 107, 0.4)' : '#45446b'};

  /* Cursor */
  cursor: ${props => props.$disabled || props.$loading || props.$noResults ? 'default' : 'pointer'};

  /* States */
  background: ${props => props.$focused && !props.$disabled ? 'rgba(69, 68, 107, 0.1)' : 'transparent'};

  /* Hover */
  &:hover {
    background: ${props => props.$disabled || props.$loading || props.$noResults ? 'transparent' : 'rgba(69, 68, 107, 0.1)'};
  }

  /* Border between items */
  &:not(:last-child) {
    border-bottom: 1px solid rgba(69, 68, 107, 0.1);
  }
`;

const SelectedBadge = styled.span`
  font-size: 12px;
  color: rgba(69, 68, 107, 0.6);
  font-weight: 500;
`;

const LoadingText = styled.span`
  color: rgba(69, 68, 107, 0.6);
  font-style: italic;
`;

const NoResultsText = styled.span`
  color: rgba(69, 68, 107, 0.6);
  font-style: italic;
`;

const ErrorMessage = styled.div`
  /* Typography */
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.2;
  color: #e03333;

  /* Layout */
  margin-top: 8px;
  padding-left: 8px;
`;

export default SearchInputWithChips;
